import { useState } from "react";
import {
  Agreement03Icon,
  Checkbox,
  FormTable,
  FormTableBody,
  Label,
} from "@porchpass/ui";

interface RegisterDisclaimerProps {
  smsOptOut: boolean;
  onSmsOptOutChange: (checked: boolean) => void;
  errorMessage?: string;
  hasAttemptedFormSubmission?: boolean;
}

const linkClassName = "font-medium text-primary";

export default ({
  smsOptOut,
  onSmsOptOutChange,
  errorMessage,
  hasAttemptedFormSubmission,
}: RegisterDisclaimerProps) => {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const isChecked = !smsOptOut;
  const showError =
    Boolean(errorMessage) &&
    !isChecked &&
    (hasUserInteracted || hasAttemptedFormSubmission);

  return (
    <FormTable
      title="Terms & Agreements"
      headerIcon={
        <Agreement03Icon
          variant="bulk-rounded"
          size={16}
          className="text-[#6F41E6]"
        />
      }
    >
      <FormTableBody className="space-y-4">
        <p className="text-sm font-medium leading-5 text-[#111827]">
          Your information is secure. By submitting,
        </p>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-5 text-[#4a5466]">
          <li>
            You agree to receive calls, texts, emails through your number and
            email address from PorchPass, its{" "}
            <a
              target="_blank"
              href="https://www.porchpass.com/affiliates"
              rel="noreferrer"
              className={linkClassName}
            >
              affiliates
            </a>
            ,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.porchpass.com/lenders"
              className={linkClassName}
            >
              lenders
            </a>{" "}
            agents and home & service providers about mobile home matters or
            promotional offers from partners, even if they use an autodialer and
            even if you are on a Do Not Call List.
          </li>
          <li>You agree to accept delivery of documents electronically.</li>
          <li>
            You agree to the{" "}
            <a
              target="_blank"
              href="https://www.porchpass.com/privacy"
              rel="noreferrer"
              className={linkClassName}
            >
              privacy policy
            </a>{" "}
            and{" "}
            <a
              target="_blank"
              href="https://www.porchpass.com/terms"
              rel="noreferrer"
              className={linkClassName}
            >
              terms
            </a>
            .
          </li>
        </ul>
        <p className="text-sm leading-5 text-[#4a5466]">
          You may receive service without this consent by contacting{" "}
          <a
            target="_blank"
            href="https://www.porchpass.com/contact"
            rel="noreferrer"
            className={linkClassName}
          >
            PorchPass
          </a>
          .
        </p>
        <div
          className="flex flex-col gap-1.5 border-t border-surface-container-disabled pt-4"
          data-form-field-error={showError ? "true" : undefined}
        >
          <div className="flex items-start gap-2">
            <Checkbox
              id="sms_notification_agreement"
              checked={isChecked}
              aria-invalid={showError ? true : undefined}
              onCheckedChange={(checked) => {
                setHasUserInteracted(true);
                onSmsOptOutChange(checked === true);
              }}
              className="mt-1 shrink-0"
            />
            <Label
              htmlFor="sms_notification_agreement"
              className="text-xs font-normal leading-4.5 text-[#4a5466] pt-px"
            >
              By checking this box, you agree to receive text messages from
              PorchPass Inc. related to your application status and processing
              at the phone number provided above. You may reply STOP to opt-out
              at any time. For assistance reply HELP. Messages and data rates
              may apply. Message frequency will vary.
            </Label>
          </div>
          {showError && (
            <p className="ml-6 text-3xs text-[#872525]">{errorMessage}</p>
          )}
        </div>
      </FormTableBody>
    </FormTable>
  );
};
